import React, { Component } from 'react';

import { Box, Typography } from '@mui/material';

import { AVAILABILITY, LUX_PLUS_SCHEDULE, ONLINE_PURCHASE } from '~/consts/schedule';

import { allRegions } from '../../../consts/allRegions';
import { brands, isMultiRegionSchedule, isUnusedBrand } from '../../../consts/brands';
import { getRegionsNameByCode } from '../../../utils/getRegionsNameByCode';
import { groupRegionsBySchedule } from '../../../utils/groupRegionsBySchedule';
import { scheduleForBrandAndType } from '../../../utils/scheduleForBrandAndType';

import ScheduleHistory from './ScheduleHistory';

type Props = {
  offerId: string;
  schedules: Array<{
    id: string;
    brand: string | App.Brands;
    regions: Array<string>;
    type: string;
    start: string;
    end: string;
  }>;
  type: string;
};

export default class Visibility extends Component<Props> {
  formatDate = (date_in: string) => {
    const date = new Date(date_in);
    return date.toDateString();
  };

  getScheduleWindow = (brandValue: string, scheduleType: string) => {
    const { schedules } = this.props;
    const filtered = schedules.filter((s) => s.brand === brandValue && s.type === scheduleType);
    if (filtered.length === 0) {
      return null;
    }

    const earliestStart = filtered.reduce((earliest, s) =>
      new Date(s.start) < new Date(earliest.start) ? s : earliest,
    );
    const latestEnd = filtered.reduce((latest, s) => (new Date(s.end) > new Date(latest.end) ? s : latest));

    return { start: earliestStart.start, end: latestEnd.end };
  };

  renderScheduleWindows = (brandValue: string) => {
    const availabilityWindow = this.getScheduleWindow(brandValue, AVAILABILITY);
    const onlinePurchaseWindow = this.getScheduleWindow(brandValue, ONLINE_PURCHASE);

    if (!availabilityWindow && !onlinePurchaseWindow) {
      return null;
    }

    return (
      <>
        {availabilityWindow && (
          <Typography variant="caption" sx={{ color: 'success.main' }}>
            Available: {this.formatDate(availabilityWindow.start)} &rarr; {this.formatDate(availabilityWindow.end)}
          </Typography>
        )}
        {onlinePurchaseWindow && (
          <Typography variant="caption" sx={{ color: 'info.main' }}>
            Online Purchase: {this.formatDate(onlinePurchaseWindow.start)} &rarr;{' '}
            {this.formatDate(onlinePurchaseWindow.end)}
          </Typography>
        )}
      </>
    );
  };

  render() {
    const { offerId, schedules, type } = this.props;
    const multiRegionSchedules = groupRegionsBySchedule(schedules, type);
    const groupedLuxPlusSchedules = groupRegionsBySchedule(schedules, LUX_PLUS_SCHEDULE);
    return (
      <div className="fp-visibility">
        {brands
          .filter((brand) => isMultiRegionSchedule(brand.value))
          .map((brand) => {
            const schedules = multiRegionSchedules.filter((offer) => offer.brand === brand.title);
            if (schedules.length > 0) {
              return schedules.map((row) => (
                <div className="schedule-pane" key={row.id}>
                  <div className="lux-schedule-row">
                    <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
                      <Typography className="title" variant="body1" sx={{ fontWeight: 600 }}>
                        {row.brand}
                      </Typography>
                      {this.renderScheduleWindows(brand.value)}
                    </Box>
                    <div className="regions">
                      {getRegionsNameByCode(row.regions, allRegions)
                        .filter((region) => !!region)
                        .join(', ')}
                    </div>
                    <div className="lux-schedule-second-column">
                      <Box className="lux-schedule-date">
                        <Box className="date-labels">
                          <Box className="date-start">{this.formatDate(row.start)}</Box>
                          <Box className="arrow">&#8594;</Box>
                          <Box className="date-end">{this.formatDate(row.end)}</Box>
                        </Box>
                        <ScheduleHistory offerId={offerId} type={type} brand="luxuryescapes" />
                      </Box>
                    </div>
                  </div>
                </div>
              ));
            }
            return (
              <div className="schedule-pane" key={brand.value}>
                <div className="lux-schedule-row">
                  <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
                    <Typography className="title" variant="body1" sx={{ fontWeight: 600 }}>
                      {brand.title}
                    </Typography>
                    {this.renderScheduleWindows(brand.value)}
                  </Box>
                  <div className="lux-schedule-date">
                    <Box className="fp-empty">No list_visibility schedule set</Box>
                    <ScheduleHistory offerId={offerId} type={type} brand={brand.value as App.Brands} />
                  </div>
                </div>
              </div>
            );
          })}
        {groupedLuxPlusSchedules.length > 0 &&
          groupedLuxPlusSchedules.map((row) => (
            <div className="schedule-pane" key={row.id}>
              <div className="lux-schedule-row">
                <span className="title">Lux Plus</span>
                <div className="regions">
                  {getRegionsNameByCode(row.regions, allRegions)
                    .filter((region) => !!region)
                    .join(', ')}
                </div>
                <div className="lux-schedule-second-column">
                  <div className="date">
                    <div className="date-start">{this.formatDate(row.start)}</div>
                    <div className="arrow">&#8594;</div>
                    <div className="date-end">{this.formatDate(row.end)}</div>
                    <ScheduleHistory offerId={offerId} type={type} brand="luxuryescapes" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        {brands
          .filter((item) => !isMultiRegionSchedule(item.value) && !isUnusedBrand(item.value))
          .map((brand) => {
            const hasScheduleForBrandAndType = scheduleForBrandAndType({
              schedules,
              brand: brand.value,
              type,
              scheduleKey: 'end',
            });
            if (hasScheduleForBrandAndType) {
              return (
                <div className="schedule-pane" key={brand.value}>
                  <div className="lux-schedule-row">
                    <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
                      <Typography className="title" variant="body1" sx={{ fontWeight: 600 }}>
                        {brand.title}
                      </Typography>
                      {this.renderScheduleWindows(brand.value)}
                    </Box>
                    <div className="lux-schedule-date">
                      <span>
                        <div className="date-labels">
                          <div className="date-start">
                            {this.formatDate(
                              scheduleForBrandAndType({
                                schedules,
                                brand: brand.value,
                                type,
                                scheduleKey: 'start',
                              }),
                            )}
                          </div>
                          <div className="arrow">&#8594;</div>
                          <div className="date-end">
                            {this.formatDate(
                              scheduleForBrandAndType({
                                schedules,
                                brand: brand.value,
                                type,
                                scheduleKey: 'end',
                              }),
                            )}
                          </div>
                        </div>
                      </span>
                      <ScheduleHistory offerId={offerId} type={type} brand={brand.value as App.Brands} />
                    </div>
                  </div>
                </div>
              );
            }
          })}
      </div>
    );
  }
}
